/**
 * CompanySettingsDialog - Dialog pentru editarea datelor firmei
 * Include lookup CUI automat, câmpuri editabile și upload logo
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Search, Save, Loader2, Upload, Trash2, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanySettingsDialog({ open, onOpenChange, companyId, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const fileInputRef = useRef(null);
  const [companyData, setCompanyData] = useState({
    name: '',
    cui: '',
    reg_com: '',
    address: '',
    city: '',
    county: '',
    phone: '',
    email: '',
    iban: '',
    bank_name: '',
    admin_name: '',
    admin_function: 'Administrator',
    logo_url: ''
  });

  // Încarcă datele existente
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!open || !companyId) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/companies/${companyId}`);
        setCompanyData({
          name: response.data.name || '',
          cui: response.data.cui || '',
          reg_com: response.data.reg_com || '',
          address: response.data.address || '',
          city: response.data.city || '',
          county: response.data.county || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          iban: response.data.iban || response.data.bank_account || '',
          bank_name: response.data.bank_name || '',
          admin_name: response.data.admin_name || '',
          admin_function: response.data.admin_function || 'Administrator',
          logo_url: response.data.logo_url || ''
        });
      } catch (error) {
        console.error("Error fetching company data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyData();
  }, [open, companyId]);

  // Lookup CUI
  const handleLookupCUI = async () => {
    if (!companyData.cui) {
      toast.error("Introduceți CUI-ul");
      return;
    }
    
    setLookingUp(true);
    try {
      // Încercăm openapi.ro mai întâi
      const res = await axios.get(`${API}/clients/lookup/${companyData.cui}?company_id=${companyId}`);
      const { data } = res.data;
      
      if (data) {
        setCompanyData(prev => ({
          ...prev,
          name: data.name || data.denumire || prev.name,
          reg_com: data.reg_com || data.numar_reg_com || prev.reg_com,
          address: data.address || data.adresa || prev.address,
          city: data.city || data.localitate || prev.city,
          county: data.county || data.judet || prev.county
        }));
        toast.success("Date firmă preluate automat!");
      }
    } catch (error) {
      // Fallback la superadmin lookup
      try {
        const fallbackRes = await axios.post(`${API}/superadmin/lookup-cui`, { cui: companyData.cui });
        if (fallbackRes.data.found) {
          setCompanyData(prev => ({
            ...prev,
            name: fallbackRes.data.company_name || prev.name,
            reg_com: fallbackRes.data.reg_com || prev.reg_com,
            address: fallbackRes.data.address || prev.address
          }));
          toast.success("Date firmă preluate!");
        } else {
          toast.error("CUI negăsit. Completați manual.");
        }
      } catch (e) {
        toast.error("Eroare la căutare CUI");
      }
    } finally {
      setLookingUp(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validare tip fișier
    if (!file.type.startsWith('image/')) {
      toast.error("Selectați o imagine (PNG, JPG, etc.)");
      return;
    }
    
    // Validare dimensiune (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("Logo-ul trebuie să fie mai mic de 500KB");
      return;
    }
    
    // Conversie la base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setCompanyData(prev => ({
        ...prev,
        logo_url: event.target?.result
      }));
      toast.success("Logo încărcat!");
    };
    reader.readAsDataURL(file);
  };

  // Ștergere logo
  const handleRemoveLogo = () => {
    setCompanyData(prev => ({ ...prev, logo_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success("Logo șters");
  };

  // Salvare
  const handleSave = async () => {
    if (!companyData.name || !companyData.cui) {
      toast.error("Completați cel puțin denumirea și CUI-ul");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${companyId}`, companyData);
      toast.success("Date firmă salvate!");
      onSave && onSave(companyData);
      onOpenChange(false);
    } catch (error) {
      toast.error("Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Setări Date Firmă
          </DialogTitle>
          <DialogDescription>
            Completați datele firmei pentru a fi folosite în documente, facturi și contracte
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Secțiunea Logo */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
              <h3 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Logo Firmă
              </h3>
              <div className="flex items-center gap-4">
                {/* Preview Logo */}
                <div className="w-24 h-24 border-2 border-dashed border-indigo-300 rounded-lg flex items-center justify-center bg-white overflow-hidden">
                  {companyData.logo_url ? (
                    <img 
                      src={companyData.logo_url} 
                      alt="Logo firmă" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center text-indigo-400">
                      <Image className="w-8 h-8 mx-auto mb-1" />
                      <span className="text-xs">Fără logo</span>
                    </div>
                  )}
                </div>
                
                {/* Butoane upload/ștergere */}
                <div className="flex flex-col gap-2">
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/*" 
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {companyData.logo_url ? 'Schimbă Logo' : 'Încarcă Logo'}
                  </Button>
                  {companyData.logo_url && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={handleRemoveLogo}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Șterge Logo
                    </Button>
                  )}
                  <p className="text-xs text-indigo-500 mt-1">
                    PNG, JPG, max 500KB. Va apărea în antetul documentelor.
                  </p>
                </div>
              </div>
            </div>

            {/* Secțiunea CUI cu lookup */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-700 mb-3">Identificare Firmă</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CUI / Cod Fiscal *</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={companyData.cui}
                      onChange={(e) => setCompanyData({...companyData, cui: e.target.value.replace(/[^0-9]/g, '')})}
                      placeholder="12345678"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleLookupCUI}
                      disabled={lookingUp}
                      className="border-purple-300 text-purple-700"
                    >
                      {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    Introduceți CUI și apăsați 🔍 pentru completare automată
                  </p>
                </div>
                <div>
                  <Label>Nr. Registru Comerț</Label>
                  <Input 
                    value={companyData.reg_com}
                    onChange={(e) => setCompanyData({...companyData, reg_com: e.target.value})}
                    placeholder="J22/1234/2020"
                  />
                </div>
              </div>
            </div>

            {/* Denumire și adresă */}
            <div className="space-y-4">
              <div>
                <Label>Denumire Firmă *</Label>
                <Input 
                  value={companyData.name}
                  onChange={(e) => setCompanyData({...companyData, name: e.target.value})}
                  placeholder="S.C. EXEMPLU S.R.L."
                />
              </div>
              
              <div>
                <Label>Adresă Sediu Social</Label>
                <Input 
                  value={companyData.address}
                  onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                  placeholder="Str. Exemplu, Nr. 1, Bl. A, Sc. B, Ap. 10"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Localitate</Label>
                  <Input 
                    value={companyData.city}
                    onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
                    placeholder="Iași"
                  />
                </div>
                <div>
                  <Label>Județ</Label>
                  <Input 
                    value={companyData.county}
                    onChange={(e) => setCompanyData({...companyData, county: e.target.value})}
                    placeholder="Iași"
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-700 mb-3">Date Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telefon</Label>
                  <Input 
                    value={companyData.phone}
                    onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                    placeholder="0755 123 456"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={companyData.email}
                    onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                    placeholder="contact@firma.ro"
                  />
                </div>
              </div>
            </div>

            {/* Date bancare */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-700 mb-3">Date Bancare</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>IBAN</Label>
                  <Input 
                    value={companyData.iban}
                    onChange={(e) => setCompanyData({...companyData, iban: e.target.value.toUpperCase()})}
                    placeholder="RO00XXXX0000000000000000"
                  />
                </div>
                <div>
                  <Label>Bancă</Label>
                  <Input 
                    value={companyData.bank_name}
                    onChange={(e) => setCompanyData({...companyData, bank_name: e.target.value})}
                    placeholder="Banca Transilvania"
                  />
                </div>
              </div>
            </div>

            {/* Reprezentant */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-700 mb-3">Reprezentant Legal</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nume și Prenume</Label>
                  <Input 
                    value={companyData.admin_name}
                    onChange={(e) => setCompanyData({...companyData, admin_name: e.target.value})}
                    placeholder="Popescu Ion"
                  />
                </div>
                <div>
                  <Label>Funcție</Label>
                  <Input 
                    value={companyData.admin_function}
                    onChange={(e) => setCompanyData({...companyData, admin_function: e.target.value})}
                    placeholder="Administrator"
                  />
                </div>
              </div>
            </div>

            {/* Butoane */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Anulează
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salvează
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
